import { Link } from "react-router-dom";
import { Check, X, Lightning, ArrowRight, Trophy, Crosshair, ChartBar } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";

const handleSignIn = () => {
  const r = window.location.origin + "/";
  window.location.href = '/api/auth/google/login';
};

const FEATURES = [
  { name: "Trade Calculator", free: true, pro: true, comm: true },
  { name: "Player values & rankings", free: true, pro: true, comm: true },
  { name: "Research & guides", free: true, pro: true, comm: true },
  { name: "Number of leagues", free: "1", pro: "Unlimited", comm: "Unlimited" },
  { name: "AI trade analyses", free: "3/month", pro: "Unlimited", comm: "Unlimited" },
  { name: "AI Trade Verdict (buy/hold/sell)", free: false, pro: true, comm: true },
  { name: "Championship Blueprint", free: "Teaser", pro: "Full", comm: "Full" },
  { name: "Trade Finder", free: false, pro: true, comm: true },
  { name: "Contender Rankings", free: false, pro: true, comm: true },
  { name: "Roster Optimizer / Simulator", free: false, pro: true, comm: true },
  { name: "Trade Partner Matching", free: false, pro: true, comm: true },
  { name: "League-wide Trade Review", free: false, pro: false, comm: true },
  { name: "League Health Dashboard", free: false, pro: false, comm: true },
  { name: "Multi-team commissioner view", free: false, pro: false, comm: true },
];

function CellVal({ val }) {
  if (val === true) return <Check size={16} weight="bold" className="text-[#4ade80] mx-auto" />;
  if (val === false) return <X size={14} weight="bold" className="text-[#555] mx-auto" />;
  return <span className="text-xs text-white font-semibold">{val}</span>;
}

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Pricing — Dynasty Trade Maker" description="Win your dynasty league with data, not guesses. Free tools forever. Pro at $29/year with unlimited leagues and AI trade analysis." path="/pricing" />
      <PublicNav />

      <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        {/* Outcome-first narrative */}
        <div className="text-center mb-14">
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-white uppercase mt-2 mb-4" data-testid="pricing-title">
            WIN YOUR DYNASTY LEAGUE.<br /><span className="text-[#ccff00]">WITH DATA, NOT GUESSES.</span>
          </h1>
          <p className="text-base text-[#a3a3a3] max-w-2xl mx-auto">Every other tool shows you a value chart. Dynasty Trade Maker gives you AI trade packages built for your roster, the real trade market, and your team graded against 6,976 real champions.</p>
        </div>

        {/* Three outcome blocks */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-14">
          {[
            { icon: <Crosshair size={20} weight="bold" />, title: "Never get fleeced again.", desc: "Instant fairness verdicts on any trade, powered by real dynasty trade data, not a static chart." },
            { icon: <ChartBar size={20} weight="bold" />, title: "Find the trades that win.", desc: "AI scans every team and hands you deals built for your roster." },
            { icon: <Trophy size={20} weight="bold" />, title: "Build like a champion.", desc: "Grade your roster against 6,976 real title teams and see exactly what you're missing." },
          ].map(b => (
            <div key={b.title} className="border border-[#262626] bg-[#171717] p-5">
              <div className="text-[#ccff00] mb-2">{b.icon}</div>
              <h3 className="text-sm font-bold text-white mb-1">{b.title}</h3>
              <p className="text-xs text-[#888]">{b.desc}</p>
            </div>
          ))}
        </div>

        {/* Tier Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-testid="tier-cards">
          {/* FREE */}
          <div className="border border-[#262626] bg-[#171717] p-6 flex flex-col">
            <h2 className="text-lg font-bold text-white mb-1">Free</h2>
            <p className="text-[10px] text-[#888] uppercase tracking-wider mb-3">Forever. No account needed.</p>
            <p className="font-mono font-bold text-4xl text-white mb-1">$0</p>
            <p className="text-[10px] text-[#666] mb-5">Free forever</p>
            <div className="space-y-2 mb-6 flex-1">
              {["Trade Calculator (unlimited)", "Player values & rankings", "Research & guides", "3 AI trade analyses/month", "Championship Blueprint (teaser)"].map(f => (
                <div key={f} className="flex items-start gap-2 text-xs text-[#a3a3a3]"><Check size={12} weight="bold" className="text-[#4ade80] mt-0.5 shrink-0" />{f}</div>
              ))}
            </div>
            <Link to="/trade-calculator" className="w-full h-10 flex items-center justify-center bg-[#262626] text-white font-bold text-[11px] uppercase tracking-wider rounded-sm no-underline hover:bg-[#333] transition-colors">START FREE</Link>
          </div>

          {/* PRO */}
          <div className="border-2 border-[#ccff00] bg-[#171717] p-6 flex flex-col relative" data-testid="plan-pro">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ccff00] text-black text-[9px] font-bold uppercase tracking-wider px-3 py-1">MOST POPULAR</div>
            <h2 className="text-lg font-bold text-white mb-1">Pro</h2>
            <p className="text-[10px] text-[#ccff00] uppercase tracking-wider mb-3 font-bold">Unlimited leagues</p>
            <p className="font-mono font-bold text-4xl text-[#ccff00] mb-1">$29<span className="text-lg text-[#888]">/yr</span></p>
            <p className="text-[10px] text-[#666] mb-1">$2.42/month, billed annually</p>
            <p className="text-[10px] text-[#555] mb-5">or $4.99/mo billed monthly</p>
            <div className="space-y-2 mb-6 flex-1">
              {["Everything in Free", "UNLIMITED leagues", "Unlimited AI trade analyses", "AI Trade Verdict (buy/hold/sell)", "Full Championship Blueprint", "Trade Finder (auto-scan deals)", "Contender Rankings", "Trade Simulator & Roster Optimizer", "Trade Partner Matching", "Full value trends & history"].map(f => (
                <div key={f} className="flex items-start gap-2 text-xs text-[#a3a3a3]"><Check size={12} weight="bold" className="text-[#ccff00] mt-0.5 shrink-0" /><span className={f === "UNLIMITED leagues" ? "text-white font-bold" : ""}>{f}</span></div>
              ))}
            </div>
            <button onClick={handleSignIn} className="w-full h-10 bg-[#ccff00] text-black font-bold text-[11px] uppercase tracking-wider rounded-sm hover:bg-[#b8e600] transition-colors" data-testid="plan-cta-pro">GET PRO</button>
          </div>

          {/* COMMISSIONER */}
          <div className="border border-[#262626] bg-[#171717] p-6 flex flex-col" data-testid="plan-commissioner">
            <h2 className="text-lg font-bold text-white mb-1">Commissioner</h2>
            <p className="text-[10px] text-[#888] uppercase tracking-wider mb-3">Run a better league</p>
            <p className="font-mono font-bold text-4xl text-white mb-1">$59<span className="text-lg text-[#888]">/yr</span></p>
            <p className="text-[10px] text-[#666] mb-5">$4.92/month, billed annually</p>
            <div className="space-y-2 mb-6 flex-1">
              {["Everything in Pro", "League-wide Trade Review", "Trade Fairness Analyzer (league scale)", "League Health Dashboard", "Multi-team commissioner view", "Shareable league reports"].map(f => (
                <div key={f} className="flex items-start gap-2 text-xs text-[#a3a3a3]"><Check size={12} weight="bold" className="text-[#4ecdc4] mt-0.5 shrink-0" />{f}</div>
              ))}
            </div>
            <button onClick={handleSignIn} className="w-full h-10 bg-[#262626] text-white font-bold text-[11px] uppercase tracking-wider rounded-sm hover:bg-[#333] transition-colors" data-testid="plan-cta-commissioner">GET COMMISSIONER</button>
          </div>
        </div>

        {/* Draft Kit */}
        <div className="border border-[#f9ca24]/30 bg-[#f9ca24]/5 p-6 mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="draft-kit">
          <div>
            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#f9ca24]">SEASONAL</span>
            <h3 className="font-heading text-xl text-white uppercase mt-1 mb-1">THE DRAFT KIT — $19 <span className="text-sm text-[#888] font-mono">one-time</span></h3>
            <p className="text-xs text-[#888] max-w-lg">Startup draft rankings & tiers, rookie pick values, data-backed guides, Championship Blueprint access, downloadable cheat sheets. Perfect for draft season — apply your $19 toward a Pro subscription anytime.</p>
          </div>
          <button onClick={handleSignIn} className="h-10 px-6 bg-[#f9ca24] text-black font-bold text-[11px] uppercase tracking-wider rounded-sm hover:bg-[#e6b620] transition-colors shrink-0 whitespace-nowrap" data-testid="plan-cta-draft-kit">GET THE DRAFT KIT</button>
        </div>

        {/* Feature Comparison Table */}
        <div className="mb-10">
          <h2 className="font-heading text-2xl text-white uppercase text-center mb-6">FEATURE COMPARISON</h2>
          <div className="border border-[#262626] bg-[#171717] overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#262626]">
                  <th className="text-left py-3 px-4 text-[#888] font-semibold w-1/2 sm:w-auto">Feature</th>
                  <th className="py-3 px-3 text-center text-[#888] font-semibold">Free</th>
                  <th className="py-3 px-3 text-center text-[#ccff00] font-semibold">Pro</th>
                  <th className="py-3 px-3 text-center text-[#4ecdc4] font-semibold">Commissioner</th>
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((f, i) => (
                  <tr key={i} className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a]/50">
                    <td className="py-2.5 px-4 text-[#a3a3a3]">{f.name}</td>
                    <td className="py-2.5 px-3 text-center"><CellVal val={f.free} /></td>
                    <td className="py-2.5 px-3 text-center bg-[#ccff00]/[0.02]"><CellVal val={f.pro} /></td>
                    <td className="py-2.5 px-3 text-center"><CellVal val={f.comm} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mb-12">
          <h2 className="font-heading text-2xl text-white uppercase text-center mb-6">QUESTIONS</h2>
          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {[
              { q: "What do I get for free?", a: "The trade calculator, dynasty value chart, player profiles, comparisons, trade explorer, research, and guides are completely free. You also get 3 AI trade analyses per month to try the core value." },
              { q: "Why unlimited leagues?", a: "Most dynasty managers play in 3-5 leagues. We removed league limits so you never have to choose which league gets the tools. One price, all your leagues." },
              { q: "Can I cancel anytime?", a: "Yes. Your access stays active until the end of your billing period. No auto-renewal surprises." },
              { q: "What is the Commissioner tier?", a: "It's for the person who runs the league. You get league-wide trade review, fairness scoring, competitive balance dashboards, and shareable reports. One commissioner influences 10-12 managers." },
              { q: "What is the Draft Kit?", a: "A $19 one-time purchase for draft season. Includes startup rankings, rookie pick values, data-backed guides, and downloadable cheat sheets. You can apply your $19 toward a Pro subscription." },
              { q: "Monthly or annual?", a: "Annual is $29/year ($2.42/mo). Monthly is $4.99/mo (~$60/year). Annual is the obvious value — and the most popular choice." },
            ].map((f, i) => (
              <div key={i} className="border border-[#262626] bg-[#171717] p-4" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <p className="text-sm text-white font-semibold mb-1" itemProp="name">{f.q}</p>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-xs text-[#888]" itemProp="text">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <p className="text-sm text-[#888] mb-4">Try the free tools first. Upgrade when you are ready.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/trade-calculator" className="h-10 px-6 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-2 no-underline font-bold hover:bg-[#b8e600] transition-colors"><Lightning size={14} weight="fill" /> FREE TRADE CALCULATOR</Link>
            <Link to="/features" className="text-xs text-[#ccff00] no-underline hover:underline flex items-center gap-1">See all features <ArrowRight size={12} /></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
